import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import styles from './activityTab.module.scss';
import {
  SingleValue,
  OptionType,
  GeneralSelect,
} from 'v2/form/CustomSelectors/GeneralSelect';
import CreateActivityModal from 'v2/common/CreateActivityModal/CreateActivityModal';
import AddAssociationButton from 'v2/common/DRM/AddAssociationButton/AddAssociationButton';
import { ActivityAssociation } from 'v2/models';
import { useQueryClient } from 'react-query';
import searchIcon from 'v2/icons/search.svg';
import SearchBar from 'v2/common/SearchBar/SearchBar';
import {
  IUseGetActivityProps,
  getInvalidationsForNewActivity,
  useGetActivities,
} from 'v2/hooks/useGetActivities';

export interface ActivityTabHeaderProps {
  listDescriptor: IUseGetActivityProps;
  filterOnChange: (value: SingleValue<OptionType>) => void;
  filterOnSearch: (searchText: string) => void;
  defaultAssociations?: ActivityAssociation[];
  showCreateModal: boolean;
  onCreateModalClose?: () => void;
}

const options: OptionType[] = [
  { label: 'All Activity', value: '' },
  { label: 'Meeting', value: 'meeting' },
  { label: 'Email', value: 'email' },
  { label: 'Data Evaluation', value: 'data_evaluation' },
  { label: 'Note', value: 'note' },
  { label: 'Response to Request', value: 'responded_to_request' },
  { label: 'Deleted file', value: 'file_deleted' },
  { label: 'Created file', value: 'file_created' },
];

const ActivityTabHeader = ({
  listDescriptor,
  filterOnChange,
  filterOnSearch,
  showCreateModal,
  defaultAssociations,
  onCreateModalClose,
}: ActivityTabHeaderProps) => {
  const queryClient = useQueryClient();
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [filterSearchText, setFilterSearchText] = useState('');
  const [filterSelectedOption, setFilterSelectedOption] = useState<
    SingleValue<OptionType>
  >(options[0]);
  const [showCreateActivityModal, setShowCreateActivityModal] =
    useState(showCreateModal);

  useEffect(() => {
    setShowCreateActivityModal(showCreateModal);
  }, [showCreateModal]);

  const { data } = useGetActivities(listDescriptor);
  const displayedActivityCount = data?.pages[0].count ?? 0;
  const displayAddButton = displayedActivityCount > 0;

  const handleFilterChange = (selectedOption: SingleValue<OptionType>) => {
    setFilterSelectedOption(selectedOption);
    filterOnChange(selectedOption);
  };

  const handleFilterOnSearch = (searchText: string) => {
    setFilterSearchText(searchText);
    filterOnSearch(searchText);
  };

  const closeCreateActivityModal = () => {
    onCreateModalClose?.();
    setShowCreateActivityModal(false);
    const key = getInvalidationsForNewActivity(listDescriptor.relatedTable);
    queryClient.invalidateQueries(key);
  };

  const cancelSearch = () => {
    handleFilterOnSearch('');
    setShowSearchBar(false);
  };

  const render = () => {
    if (showSearchBar) {
      return (
        <div className={styles.searchHeader}>
          <SearchBar
            placeHolder={`Search`}
            onSearch={(searchText) => handleFilterOnSearch(searchText)}
            currentText={filterSearchText}
            classNameBar={styles.searchBox}
            classNameIcon={styles.searchIcon}
            autoFocus={true}
            showOwnIcon={true}
            lengthTextValidation={{
              number: 1,
              operator: '!==',
              debounce: 500,
            }}
          />
          <span className={styles.cancelSearch} onClick={cancelSearch}>
            Cancel
          </span>
        </div>
      );
    }

    return (
      <div className={classnames(styles.controlPanel)}>
        <div>
          {displayAddButton && (
            <AddAssociationButton
              onClick={() => {
                setShowCreateActivityModal(true);
              }}
              label="Add Activity"
            />
          )}
          {showCreateActivityModal && (
            <CreateActivityModal
              defaultAssociations={defaultAssociations}
              onClose={closeCreateActivityModal}
            />
          )}
        </div>
        <div className="d-flex">
          <div className={classnames(styles.dropdownActivities)}>
            <GeneralSelect
              options={options}
              handleChange={handleFilterChange}
              selectedOption={filterSelectedOption}
              placeHolder="All Activity"
              disabled={false}
              data-cy="activity:activity-tab-filter-by-type"
            />
          </div>
          <div
            className={styles.searchIconContainer}
            onClick={() => setShowSearchBar(true)}
          >
            <img src={searchIcon} alt="search" draggable="false" />
          </div>
        </div>
      </div>
    );
  };

  return <div className={styles.root}>{render()}</div>;
};

export default ActivityTabHeader;
